import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toArray from "@babel/runtime/helpers/esm/toArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import { EditorState, Modifier } from 'draft-js';
import { List } from 'immutable'; // DOMAIN-SPECIFIC HELPERS

var filterEntity = {
  type: 'typeahead-filter',
  mutability: 'MUTABLE'
};
var startEntity = {
  type: 'typeahead-start',
  mutability: 'IMMUTABLE'
};
var selectionEntity = {
  type: 'typeahead-selection',
  mutability: 'IMMUTABLE'
};

var matches = function matches(searcher, text) {
  return text.toLowerCase().indexOf(searcher.toLowerCase()) > -1;
};

export var nextTypeaheadItem = function nextTypeaheadItem(editorState) {
  var filterEntity = getEntities(editorState).last();
  var data = filterEntity.data;
  var active = (data.active + 1) % data.options.size;
  return applyEntity(_objectSpread(_objectSpread({}, filterEntity), {}, {
    key: undefined,
    data: _objectSpread(_objectSpread({}, data), {}, {
      active: active
    })
  }))(editorState);
};
export var previousTypeaheadItem = function previousTypeaheadItem(editorState) {
  var filterEntity = getEntities(editorState).last();
  var data = filterEntity.data;
  var n = data.options.size; // https://dev.to/maurobringolf/a-neat-trick-to-compute-modulo-of-negative-numbers-111e

  var active = ((data.active - 1) % n + n) % n;
  return applyEntity(_objectSpread(_objectSpread({}, filterEntity), {}, {
    key: undefined,
    data: _objectSpread(_objectSpread({}, data), {}, {
      active: active
    })
  }))(editorState);
};
export var traverseBindings = function traverseBindings(bindings, _ref) {
  var _ref2 = _toArray(_ref),
      head = _ref2[0],
      tail = _ref2.slice(1);

  return head ? traverseBindings(bindings.get(head).get('children'), tail) : bindings;
};
export var checkSelectionEntities = function checkSelectionEntities(editorState) {
  var entities = getEntities(editorState);

  if (!entities.isEmpty()) {
    var validEntities = entities.reduce(function (reduction, entity) {
      return reduction.isEmpty() || entity.data.index === reduction.last().data.index + 1 ? reduction.push(entity) : reduction;
    }, List());

    if (validEntities.last().end !== entities.last().end) {
      var start = validEntities.last().end;
      var end = entities.last().end;
      var index = validEntities.last().data.index + 1;
      return apply(editorState, select({
        anchor: start,
        focus: end
      }), insertText({
        text: ' ',
        entity: _objectSpread(_objectSpread({}, filterEntity), {}, {
          data: {
            index: index
          }
        })
      }), select({
        anchor: function anchor(offset) {
          return offset - 1;
        },
        focus: function focus(offset) {
          return offset - 1;
        }
      }));
    }
  }

  return editorState;
};
export var applyFilter = function applyFilter(bindings) {
  return function (editorState) {
    var entities = getEntities(editorState);

    if (!entities.isEmpty()) {
      var selections = entities.rest().butLast().map(function (entity) {
        return entity.text;
      });

      var _entities$last = entities.last(),
          end = _entities$last.end,
          start = _entities$last.start,
          filter = _entities$last.text,
          index = _entities$last.data.index;

      var options = traverseBindings(bindings, selections.toArray()).entrySeq().toList().filter(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 1),
            label = _ref4[0];

        return matches(filter, label);
      }).map(function (_ref5) {
        var _ref6 = _slicedToArray(_ref5, 2),
            label = _ref6[0],
            value = _ref6[1];

        return value.set('label', label).toObject();
      });

      if (options.size === 0 && entities.size === 2) {
        return closeTypeahead(editorState);
      } else {
        var data = {
          index: index,
          options: options,
          active: 0
        };
        return applyEntity(_objectSpread(_objectSpread({}, filterEntity), {}, {
          start: start,
          end: end,
          data: data
        }))(editorState);
      }
    }

    return editorState;
  };
};
export var checkSelectionPosition = function checkSelectionPosition(editorState) {
  var entities = getEntities(editorState);

  if (!entities.isEmpty()) {
    var selection = editorState.getSelection();
    var start = entities.first().start;
    var end = entities.last().end;

    if (selection.getStartOffset() <= start || selection.getEndOffset() >= end) {
      return closeTypeahead(editorState);
    }
  }

  return editorState;
};
export var checkFocus = function checkFocus(editorState) {
  var entities = getEntities(editorState);

  if (!entities.isEmpty()) {
    return editorState.getSelection().getHasFocus() ? editorState : closeTypeahead(editorState);
  }

  return editorState;
};
export var startTypeahead = function startTypeahead(editorState) {
  return apply(editorState, insertText({
    text: '$',
    entity: _objectSpread(_objectSpread({}, startEntity), {}, {
      data: {
        index: 0
      }
    })
  }), insertText({
    text: ' ',
    entity: _objectSpread(_objectSpread({}, filterEntity), {}, {
      data: {
        index: 1
      }
    })
  }), select({
    anchor: function anchor(offset) {
      return offset - 1;
    },
    focus: function focus(offset) {
      return offset - 1;
    }
  }));
};
export var closeTypeahead = function closeTypeahead(editorState) {
  var entities = getEntities(editorState);
  var start = entities.first().start;
  var end = entities.last().end;
  var text = entities.size === 1 ? entities.first().text : entities.size === 2 ? "".concat(entities.first().text).concat(entities.last().text) : '';
  return apply(editorState, select({
    anchor: start,
    focus: end
  }), insertText({
    text: text
  }));
};
export var selectTypeaheadItem = function selectTypeaheadItem(self) {
  return function (label, value, selection) {
    return function () {
      // determine how the selected value should be wrapped based on the language of
      // this CodeInput instance,
      var text = self.props.language === 'js-template' ? "${".concat(value, "}") : self.props.language === 'erb' ? "<%= ".concat(value, " %>") : value; // when a selection is passed with a value we need to accommodate for the
      // number of characters we add when we wrap the value

      var templateOffset = self.props.language === 'js-template' ? 2 : self.props.language === 'erb' ? 4 : 0;
      var entities = getEntities(self.state.editorState);

      if (!value) {
        var _entities$last2 = entities.last(),
            start = _entities$last2.start,
            end = _entities$last2.end,
            data = _entities$last2.data;

        self.onChange(apply(self.state.editorState, select({
          anchor: start,
          focus: end
        }), insertText({
          text: label,
          entity: _objectSpread(_objectSpread({}, selectionEntity), {}, {
            data: {
              index: data.index
            }
          })
        }), insertText({
          text: ' ',
          entity: _objectSpread(_objectSpread({}, filterEntity), {}, {
            data: {
              index: data.index + 1
            }
          })
        }), select({
          anchor: function anchor(offset) {
            return offset - 1;
          },
          focus: function focus(offset) {
            return offset - 1;
          }
        })));
      } else {
        var _start = entities.first().start;
        var _end = entities.last().end;
        self.onChange(apply(self.state.editorState, select({
          anchor: _start,
          focus: _end
        }), insertText({
          text: text
        }), selection && select({
          anchor: _start + selection.get('start') + templateOffset,
          focus: _start + selection.get('end') + templateOffset
        })));
      }
    };
  };
};
export var getEntities = function getEntities(editorState) {
  return getEntitiesImpl(editorState.getCurrentContent().getFirstBlock(), editorState.getCurrentContent());
};
export var getEntitiesImpl = function getEntitiesImpl(block, content) {
  var text = block.getText();
  var ranges = [];
  block.findEntityRanges(function (character) {
    return character.getEntity();
  }, function (start, end) {
    var key = block.getEntityAt(start);
    ranges.push({
      start: start,
      end: end,
      key: key,
      text: text.slice(start, end),
      data: content.getEntity(key).getData(),
      type: content.getEntity(key).getType(),
      mutability: content.getEntity(key).getMutability()
    });
  });
  return List(ranges).map(function (range, i) {
    if (range.type === 'typeahead-filter') {
      var start = i === 0 ? range.start : ranges[i - 1].end;
      return _objectSpread(_objectSpread({}, range), {}, {
        start: start,
        text: text.slice(start, range.end).replace(/\s$/, '')
      });
    } else {
      return range;
    }
  });
}; // SUPER GENERIC HELPERS

export var apply = function apply(editorState) {
  for (var _len = arguments.length, operations = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    operations[_key - 1] = arguments[_key];
  }

  return operations.filter(function (op) {
    return !!op;
  }).reduce(function (reduction, op) {
    return op(reduction);
  }, editorState);
};
export var select = function select(_ref7) {
  var anchor = _ref7.anchor,
      focus = _ref7.focus;
  return function (editorState) {
    var selection0 = editorState.getSelection();
    var selection1 = selection0.set('anchorOffset', typeof anchor === 'number' ? anchor : typeof anchor === 'function' ? anchor(selection0.getAnchorOffset()) : selection0.getAnchorOffset());
    var selection2 = selection1.set('focusOffset', typeof focus === 'number' ? focus : typeof focus === 'function' ? focus(selection1.getFocusOffset()) : selection1.getFocusOffset());
    return EditorState.forceSelection(editorState, selection2);
  };
};
export var insertText = function insertText(_ref8) {
  var text = _ref8.text,
      entity = _ref8.entity;
  return function (editorState) {
    var modifier = editorState.getSelection().isCollapsed() ? Modifier.insertText : Modifier.replaceText;
    var contentState0 = editorState.getCurrentContent();
    var contentState1 = entity ? contentState0.createEntity(entity.type, entity.mutability, entity.data) : contentState0;
    var contentState2 = modifier(contentState1, editorState.getSelection(), text, null, entity ? contentState1.getLastCreatedEntityKey() : null);
    return EditorState.push(editorState, contentState2, 'insert-characters', true);
  };
};
export var applyEntity = function applyEntity(_ref9) {
  var data = _ref9.data,
      end = _ref9.end,
      key = _ref9.key,
      mutability = _ref9.mutability,
      start = _ref9.start,
      type = _ref9.type;
  return function (editorState) {
    var contentState0 = editorState.getCurrentContent();
    var contentState1 = key ? contentState0 : contentState0.createEntity(type, mutability, data);
    var entityKey = key || contentState1.getLastCreatedEntityKey();
    var selection = editorState.getSelection().set('anchorOffset', start).set('focusOffset', end);
    var contentState2 = Modifier.applyEntity(contentState1, selection, entityKey);
    return EditorState.forceSelection(EditorState.push(editorState, contentState2, 'apply-entity'), editorState.getSelection());
  };
};
export var findByEntityType = function findByEntityType(type) {
  return function (contentBlock, callback, contentState) {
    return contentBlock.findEntityRanges(function (_char) {
      return _char.getEntity() ? contentState.getEntity(_char.getEntity()).getType() === type : false;
    }, callback);
  };
};
export var getCurrentIndentation = function getCurrentIndentation(newLine, editorState) {
  var text = editorState.getCurrentContent().getBlockForKey(editorState.getSelection().getStartKey()).getText();
  var textBeforeCursor = text.slice(0, editorState.getSelection().getStartOffset());
  var lineBeforeCursor = textBeforeCursor.slice(textBeforeCursor.lastIndexOf(newLine) + 1);
  return lineBeforeCursor.replace(/\S.*$/, '');
};